/**
 * Analytics utility functions for tracking user events with FullStory and Amplify (Pinpoint).
 */

import {
  type BaseEventLog,
  type EventLog,
  SharedConfig,
  uuid,
} from '@fmc-digital/core-utils';

import { AUTH_APPLICATION } from '@/analytics/constants';
import { Analytics, isAmplifyAnalyticsReady } from '@/analytics/amplifyInit';

import { withAnalyticsUserContext } from './analyticsUserContext';
import {
  isFullStorySdkInitialized,
  logFullStoryUserEvent,
} from './fullstory.utils';

/** Base event fields plus optional analytics attributes (Amplify/Pinpoint allow extra keys). */
export type LogUserEventConfig = BaseEventLog & Record<string, unknown>;

/** Pinpoint / FullStory application name for registration flow events. */
export const REGISTRATION_APPLICATION = 'Registrationv3';

function toPinpointAttributeValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'boolean':
    case 'bigint':
      return String(value);
    case 'object':
      try {
        return JSON.stringify(value);
      } catch {
        return Object.prototype.toString.call(value);
      }
    case 'symbol':
      return value.toString();
    case 'function':
      return value.name || 'anonymous';
    default: {
      const exhaustiveCheck: never = value;
      return exhaustiveCheck;
    }
  }
}

function toPinpointAttributes(
  payload: Record<string, unknown>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      toPinpointAttributeValue(value),
    ]),
  );
}

function recordPinpointEvent(eventPayload: Record<string, unknown>): void {
  Analytics.record({
    name: toPinpointAttributeValue(eventPayload.action),
    attributes: toPinpointAttributes(eventPayload),
    immediate: true,
  }).catch((error: unknown) => {
    console.warn('[Analytics] Amplify record failed:', error);
  });
}

/**
 * Logs user events to Amplify (Pinpoint) and FullStory.
 * Pinpoint uses the shared Analytics instance from amplifyInit.ts.
 */
export function logUserEvent(config: LogUserEventConfig): void {
  const amplifyConfig = SharedConfig.get('amplify');
  const { loanNumber: amplifyLoanNumber, ...restAmplify } = amplifyConfig || {};
  const { type, action, description, loanNumber, ...restConfig } = config;

  const eventPayload = withAnalyticsUserContext({
    type,
    action,
    description,
    guid: uuid(),
    date: new Date().toISOString(),
    loanNumber: loanNumber || amplifyLoanNumber,
    ...restAmplify,
    ...restConfig,
  });

  logFullStoryUserEvent(eventPayload);

  if (!isAmplifyAnalyticsReady()) {
    console.warn(
      '[Analytics] Amplify not ready yet; skipping Pinpoint record for:',
      eventPayload.action,
    );
    return;
  }

  recordPinpointEvent(eventPayload);
}

/**
 * Forgot Email Analytics Event Names
 */
export enum ForgotEmailEvent {
  CLICKED = 'ForgotEmailClicked',
  DATA_VALIDATION_SUCCESS = 'ForgotEmailDataValidationSuccess',
  OTP_EMAIL_FACTOR_SELECTED = 'ForgotEmailOTPEmailFactorSelected',
  OTP_PHONE_FACTOR_SELECTED = 'ForgotEmailOTPPhoneFactorSelected',
  OTP_EMAIL_TRIGGERED = 'ForgotEmailOTPEmailTriggered',
  OTP_PHONE_TRIGGERED = 'ForgotEmailOTPPhoneTriggered',
  OTP_EMAIL_RESENT = 'ForgotEmailOTPEmailResent',
  OTP_PHONE_RESENT = 'ForgotEmailOTPPhoneResent',
  OTP_EMAIL_VALIDATION_SUCCESS = 'ForgotEmailOTPEmailValidationSuccess',
  OTP_EMAIL_VALIDATION_FAILED = 'ForgotEmailOTPEmailValidationFailed',
  OTP_PHONE_VALIDATION_SUCCESS = 'ForgotEmailPhoneOTPValidationSuccess',
  OTP_PHONE_VALIDATION_FAILED = 'ForgotEmailPhoneOTPValidationFailed',
  ACCOUNT_LOCKED = 'ForgotEmailAccountLocked',
}

/**
 * Forgot Password Analytics Event Names
 */
export enum ForgotPasswordEvent {
  CLICKED = 'ForgotPasswordClicked',
  DATA_VALIDATION_SUCCESS = 'ForgotPasswordDataValidationSuccess',
  OTP_EMAIL_FACTOR_SELECTED = 'ForgotPasswordOTPEmailFactorSelected',
  OTP_PHONE_FACTOR_SELECTED = 'ForgotPasswordOTPPhoneFactorSelected',
  OTP_EMAIL_TRIGGERED = 'ForgotPasswordOTPEmailTriggered',
  OTP_PHONE_TRIGGERED = 'ForgotPasswordOTPPhoneTriggered',
  OTP_EMAIL_RESENT = 'ForgotPasswordOTPEmailResent',
  OTP_PHONE_RESENT = 'ForgotPasswordOTPPhoneResent',
  OTP_EMAIL_VALIDATION_SUCCESS = 'ForgotPasswordOTPEmailValidationSuccess',
  OTP_EMAIL_VALIDATION_FAILED = 'ForgotPasswordOTPEmailValidationFailed',
  OTP_PHONE_VALIDATION_SUCCESS = 'ForgotPasswordPhoneOTPValidationSuccess',
  OTP_PHONE_VALIDATION_FAILED = 'ForgotPasswordPhoneOTPValidationFailed',
  ACCOUNT_LOCKED = 'ForgotPasswordAccountLocked',
  RESET_PWD_SUCCESS = 'ForgotPasswordResetpwdSuccess',
}

/**
 * Login Analytics Event Names
 */
/**
 * Registration value proposition (marketing / pre-account) events
 */
export enum RegistrationValuePropEvent {
  REGISTER_CLICK = 'RegisterClicked',
  DOWNLOAD_APP_CLICK = 'DownloadAppClicked',
}

/**
 * Download App Analytics Event Names
 */
export enum DownloadAppEvent {
  REGISTER_CLICKED = 'RegisterClicked',
  DOWNLOAD_APP_CLICKED = 'DownloadAppClicked',
  WEB_CONTINUE_CLICKED = 'WebContinueClicked',
}

export enum LoginEvent {
  CLICKED = 'LoginClicked',
  MFA_EMAIL_FACTOR_SELECTED = 'LoginMFAEmailFactorSelected',
  MFA_PHONE_FACTOR_SELECTED = 'LoginMFAPhoneFactorSelected',
  OTP_EMAIL_TRIGGERED = 'LoginEmailOTPTriggered',
  OTP_PHONE_TRIGGERED = 'LoginPhoneOTPTriggered',
  OTP_EMAIL_RESENT = 'LoginOTPEmailResent',
  OTP_PHONE_RESENT = 'LoginOTPPhoneResent',
  OTP_EMAIL_VALIDATION_SUCCESS = 'LoginEmailOTPValidationSuccess',
  OTP_EMAIL_VALIDATION_FAILED = 'LoginEmailOTPValidationFailed',
  OTP_PHONE_VALIDATION_SUCCESS = 'LoginPhoneOTPValidationSuccess',
  OTP_PHONE_VALIDATION_FAILED = 'LoginPhoneOTPValidationFailed',
  ACCOUNT_LOCKED = 'LoginAccountLocked',
  SUCCESS = 'LoginSuccess',
  TIMER_EXPIRED = 'LoginTimerExpired',
}

/**
 * Registration flow (ACP rebrand v3) analytics event names
 */
export enum RegistrationEvent {
  CREATE_ACCOUNT_CLICKED = 'CreateAccountClicked',
  EMAIL_OTP_REQUEST_SENT = 'NewAccountEmailOTPRequestSent',
  EMAIL_OTP_SUBMITTED = 'NewAccountEmailOTPSubmitted',
  EMAIL_OTP_VALIDATION_SUCCESS = 'NewAccountEmailOTPValidationSuccess',
  EMAIL_OTP_VALIDATION_FAILED = 'NewAccountEmailOTPValidationFailed',
  PHONE_OTP_REQUEST_SENT = 'NewAccountPhoneOTPRequestSent',
  PHONE_OTP_SUBMITTED = 'NewAccountPhoneOTPSubmitted',
  PHONE_OTP_VALIDATION_SUCCESS = 'NewAccountPhoneOTPValidationSuccess',
  PHONE_OTP_VALIDATION_FAILED = 'NewAccountPhoneOTPValidationFailed',
  PHONE_MFA_CONSENT_OPTED = 'NewAccountPhoneMFAConsentOpted',
  PHONE_MFA_CONSENT_OPTED_OUT = 'NewAccountPhoneMFAConsentOptedOut',
  PHONE_MFA_CONSENT_CONFIRMED = 'NewAccountPhoneMFAConsentConfirmed',
  PHONE_UPDATED = 'NewAccountPhoneUpdated',
  NPS_FEEDBACK_SENT = 'NewAccountNPSFeedbackSent',
  NPS_COMMENT_SKIP = 'NewAccountNPSCommentSkip',
  NPS_FEEDBACK_SKIP = 'NewAccountNPSFeedbackSkip',
  ANDROID_APP_CLICKED = 'NewAccountAndroidAppclicked',
  IOS_APP_CLICKED = 'NewAccountiOSAppclicked',
  CREATE_SUCCESS = 'NewAccountCreateSuccess',
  CREATE_FAILED = 'NewAccountCreateFailed',
  ASSUMPTION_CREATE_SUCCESS = 'AssumptionAccountCreateSuccess',
  ASSUMPTION_CREATE_FAILED = 'AssumptionAccountCreateFailed',
}

/**
 * Generic function to track user events to FullStory
 * @param eventName - Name of the event
 * @param attributes - Additional event attributes
 * @param errorContext - Context description for error logging
 */
function trackEvent(
  eventName: string,
  attributes?: Record<string, unknown>,
  errorContext = 'event'
): void {
  try {
    const timestamp = Date.now().toString();

    const event: EventLog = withAnalyticsUserContext({
      type: eventName,
      action: eventName,
      timestamp,
      date: `${new Date()}`,
      application: AUTH_APPLICATION,
      ...attributes,
    });

    console.log('---Analytics User Event---', event);

    if (!isFullStorySdkInitialized()) {
      console.warn(
        `[Analytics] FullStory SDK not initialized yet for event: ${eventName}. Event may not be recorded in FullStory.`,
      );
    }

    logUserEvent(event as LogUserEventConfig);

    console.log(`[Analytics] Successfully queued ${errorContext}: ${eventName}`);
  } catch (error) {
    console.error(`[Analytics] Failed to track ${errorContext}:`, error);
    console.error(`[Analytics] Event details:`, { eventName, attributes });
  }
}

/**
 * Tracks a forgot email user event to FullStory
 * @param eventName - Name of the event
 * @param attributes - Additional event attributes
 */
export function trackForgotEmailEvent(
  eventName: ForgotEmailEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(eventName, attributes, 'forgot email event');
}

/**
 * Tracks a forgot password user event to FullStory
 * @param eventName - Name of the event
 * @param attributes - Additional event attributes
 */
export function trackForgotPasswordEvent(
  eventName: ForgotPasswordEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(eventName, attributes, 'forgot password event');
}

/**
 * Tracks a login user event to FullStory
 * @param eventName - Name of the event
 * @param attributes - Additional event attributes
 */
export function trackLoginEvent(
  eventName: LoginEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(eventName, attributes, 'login event');
}

/**
 * Tracks registration value proposition events (FullStory / GTM pipeline)
 */
export function trackRegistrationValuePropEvent(
  eventName: RegistrationValuePropEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(
    eventName,
    { application: REGISTRATION_APPLICATION, ...attributes },
    'registration value prop event',
  );
}

/**
 * Tracks download app user events to FullStory
 * @param eventName - Name of the event
 * @param attributes - Additional event attributes
 */
export function trackDownloadAppEvent(
  eventName: DownloadAppEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(
    eventName,
    { application: REGISTRATION_APPLICATION, ...attributes },
    'download app event',
  );
}

/**
 * Tracks registration flow events to FullStory (ACP rebrand v3)
 */
export function trackRegistrationEvent(
  eventName: RegistrationEvent,
  attributes?: Record<string, unknown>
): void {
  trackEvent(
    eventName,
    { application: REGISTRATION_APPLICATION, ...attributes },
    'registration event',
  );
}
